// src/Dashboard/pages/user/DashboardUser/components/FavoriteEventsPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { toast } from "react-toastify";

import { getUsersEvents } from "../../../../../services/usersEventsServices";
import { getAllEvents } from "../../../../../services/eventServices";
import { toAbsoluteEventPhotoUrl } from "../../../../../services/eventPhotoServices";

import EmptyState from "./EmptyState";
import {
  extractEventId,
  formatDateLongFR,
  uniqueIds,
} from "../utils/dashboardUserUtils";

export default function FavoriteEventsPanel({ userId, onCounts }) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const [favorites, allEvents] = await Promise.all([
        getUsersEvents(userId).catch(() => []),
        getAllEvents().catch(() => []),
      ]);

      setFavoriteIds(uniqueIds(favorites, extractEventId));
      setEvents(Array.isArray(allEvents) ? allEvents : []);
    } catch (error) {
      console.error("[FavoriteEventsPanel] load error:", error);
      toast.error("Impossible de charger les évènements favoris.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === favoriteIds.length) return;

    prevCountRef.current = favoriteIds.length;
    onCounts({ total: favoriteIds.length });
  }, [favoriteIds.length, onCounts]);

  const favorites = useMemo(() => {
    if (!favoriteIds.length) return [];

    const map = new Map(events.map((event) => [Number(event.id), event]));

    return favoriteIds.map((id) => map.get(Number(id))).filter(Boolean);
  }, [favoriteIds, events]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="h-40 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <EmptyState
        icon={FaCalendarAlt}
        title="Aucun évènement favori"
        description="Marquez des évènements en favoris depuis le calendrier pour les retrouver rapidement ici."
      />
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {favorites.map((event) => {
        const title = event.titre || event.title || "Évènement";
        const img = event.photo_url || event.affiche_url || "";
        const date = event.start || event.date_event || event.date || null;
        const location = event.lieu_event || event.location || "";

        return (
          <article
            key={event.id}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <img
              src={toAbsoluteEventPhotoUrl(img) || "/img/no-image.jpg"}
              alt={title}
              className="h-40 w-full object-cover"
              loading="lazy"
              onError={(eventImg) => {
                eventImg.currentTarget.src = "/img/no-image.jpg";
              }}
            />

            <div className="p-4">
              <h3 className="line-clamp-2 font-bold text-slate-900 dark:text-white">
                {title}
              </h3>

              <div className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                <p className="flex items-center gap-2">
                  <FaCalendarAlt className="text-violet-500" />
                  {formatDateLongFR(date)}
                </p>

                <p className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-rose-500" />
                  {location || "Lieu à préciser"}
                </p>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}